import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  InputAdornment,
  alpha,
} from '@mui/material';
import {
  Save as SaveIcon,
  Inventory as InventoryIcon,
  Category as CategoryIcon,
  Numbers as NumbersIcon,
  LocationOn as LocationIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { canManageInventory } from '../../../utils/inventoryPermissions';
import Header from '../../common/Header';
import Loading from '../../common/Loading';
import FeedbackMessage from '../../common/FeedbackMessage';
import ConfirmUpdateDialog from '../../common/ConfirmDeleteDialog';
import CommonSectionCard from '../../common/SectionCard';
import SubmitActionsCard from '../../common/SubmitActionsCard';
import FormDraftNotice from '../../common/FormDraftNotice';
import useFormDraftBindings from '../../../hooks/useFormDraftBindings';
import { SUCCESS } from '../../common/SaaSTokens';
import TextField from '@mui/material/TextField';
import SearchableSelect from '../../common/SearchableSelect';
import { inputRootSx } from '../../common/formFieldSx';
import {
  formatFieldErrorsMessage,
  getFirstErrorMessage,
  rejectConfirmValidation,
  applyApiValidationErrors,
  createFieldErrorGate,
} from '../../../utils/formValidationFeedback';

// ─── Design tokens ─────────────────────────────────────────────────────────────
const NEON  = '#38BDF8';
const NEON2 = '#818CF8';
const NEON3 = '#34D399';
const SURFACE_UP = '#0f1f38';
const TEXT_BODY  = '#E2E8F0';

const FORM_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600&family=Rajdhani:wght@500;600;700&family=DM+Sans:wght@300;400;500;600&display=swap');
  @keyframes form-glow-in { from { box-shadow: 0 0 0 0 rgba(56,189,248,0.45); } to { box-shadow: 0 0 0 4px rgba(56,189,248,0); } }

  .MuiOutlinedInput-input,
  .MuiInputBase-input {
    color: #E2E8F0 !important;
    -webkit-text-fill-color: #E2E8F0 !important;
    caret-color: #38BDF8 !important;
  }
  .MuiOutlinedInput-input:-webkit-autofill,
  .MuiInputBase-input:-webkit-autofill {
    -webkit-box-shadow: 0 0 0 100px #0f1f38 inset !important;
    -webkit-text-fill-color: #E2E8F0 !important;
  }
`;

const STOCK_VALIDATION_FIELDS = new Set(['things', 'quantity', 'place', 'department_id', 'remark']);

const StyledField = ({ sx, ...props }) => (
  <TextField {...props} sx={{ ...inputRootSx, ...sx }} />
);

// ─── SectionCard ──────────────────────────────────────────────────────────────
const SectionCard = ({ title, icon, delay = 0, children }) => (
  <CommonSectionCard title={title} icon={icon} delay={delay}>
    {children}
  </CommonSectionCard>
);

// ─── Main Component ────────────────────────────────────────────────────────────
const StockForm = () => {
  const navigate   = useNavigate();
  const { id }     = useParams();
  const { user: currentUser } = useAuth();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState({
    things: '', quantity: '', place: '', department_id: '', remark: '',
  });

  const [departments,              setDepartments]              = useState([]);
  const [loading,                  setLoading]                  = useState(false);
  const [dataLoading,              setDataLoading]              = useState(true);
  const [error,                    setError]                    = useState(null);
  const [successMessage,           setSuccessMessage]           = useState('');
  const [validationErrors,         setValidationErrors]         = useState({});
  const [showFieldErrors,          setShowFieldErrors]          = useState(false);
  const [confirmUpdateDialogOpen,  setConfirmUpdateDialogOpen]  = useState(false);

  const canManageStock = canManageInventory(currentUser);

  const { formDraft, submitDraftProps } = useFormDraftBindings({
    formKey: 'stock',
    userId: currentUser?.id,
    isEditMode,
    enabled: !dataLoading,
    formData,
    setFormData,
  });

  useEffect(() => {
    const cssId = 'stockform-ds-css';
    if (!document.getElementById(cssId)) {
      const el = document.createElement('style');
      el.id = cssId; el.innerHTML = FORM_CSS;
      document.head.appendChild(el);
    }
  }, []);

  useEffect(() => {
    if (!currentUser)    { navigate('/login');     return; }
    if (!canManageStock) { navigate('/dashboard'); return; }
    fetchDepartments();
    if (isEditMode) fetchStockItem();
    else setDataLoading(false);
  }, [currentUser, id, isEditMode, canManageStock]);

  const fetchDepartments = async () => {
    try {
      const response = await api.get('/departments');
      setDepartments(response.data);
    } catch { setError('Failed to fetch departments. Please try again.'); }
  };

  const fetchStockItem = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/stocks/${id}`);
      const stock = response.data;
      setFormData({
        things:        stock.things        || '',
        quantity:      stock.quantity      || '',
        place:         stock.place         || '',
        department_id: stock.department_id != null ? String(stock.department_id) : '',
        remark:        stock.remark        || '',
      });
    } catch { setError('Failed to load stock item. Please try again.'); navigate('/stocks'); }
    finally   { setLoading(false); setDataLoading(false); }
  };

  const collectValidationErrors = () => {
    const errors = {};
    if (!formData.things?.trim()) errors.things = 'Item name is required.';

    const qtyRaw = String(formData.quantity ?? '').trim();
    if (qtyRaw === '') {
      errors.quantity = 'Quantity is required.';
    } else {
      const qty = Number.parseInt(qtyRaw, 10);
      if (Number.isNaN(qty)) errors.quantity = 'Quantity must be a whole number.';
      else if (qty < 0) errors.quantity = 'Quantity cannot be negative.';
    }

    if (!formData.place?.trim()) errors.place = 'Storage location is required.';
    if (!formData.department_id) errors.department_id = 'Department is required.';
    return errors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (error) setError(null);
    if (showFieldErrors && validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (error) setError(null);
    if (showFieldErrors && validationErrors[field]) {
      setValidationErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const buildPayload = () => ({
    ...formData,
    quantity:      parseInt(formData.quantity) || 0,
    department_id: formData.department_id || null,
  });

  const validateForm = () => {
    const errors = collectValidationErrors();
    setValidationErrors(errors);
    setShowFieldErrors(true);
    if (Object.keys(errors).length > 0) {
      setError(formatFieldErrorsMessage(errors));
      return false;
    }
    setError(null);
    return true;
  };

  const handleApiError = (err, fallbackMessage) => {
    const data = err?.response?.data;
    applyApiValidationErrors(data, setValidationErrors, STOCK_VALIDATION_FIELDS);
    const fieldMsgs = {};
    const errs = data?.errors;
    if (errs && typeof errs === 'object') {
      Object.entries(errs).forEach(([key, msgs]) => {
        if (!STOCK_VALIDATION_FIELDS.has(key)) return;
        const msg = Array.isArray(msgs) ? msgs.find(Boolean) : msgs;
        if (msg) fieldMsgs[key] = String(msg);
      });
    }
    setShowFieldErrors(true);
    setError(
      Object.keys(fieldMsgs).length
        ? formatFieldErrorsMessage(fieldMsgs)
        : (getFirstErrorMessage(data) || fallbackMessage),
    );
  };

  const doSave = async () => {
    try {
      setLoading(true);
      setError(null);
      setValidationErrors({});
      const payload = buildPayload();
      if (isEditMode) await api.put(`/stocks/${id}`, payload);
      else await api.post('/stocks', payload);
      if (!isEditMode) formDraft.clearDraft();
      setSuccessMessage(`Inventory item ${isEditMode ? 'updated' : 'created'} successfully!`);
      setTimeout(() => navigate('/stocks', { state: { refreshList: Date.now() } }), 1500);
    } catch (err) {
      handleApiError(err, `Failed to ${isEditMode ? 'update' : 'save'} stock item. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isEditMode) {
      setError(null);
      setValidationErrors({});
      setShowFieldErrors(false);
      setConfirmUpdateDialogOpen(true);
      return;
    }
    setError(null);
    if (!validateForm()) return;
    doSave();
  };

  const handleConfirmUpdate = async () => {
    const errors = collectValidationErrors();
    if (Object.keys(errors).length > 0) {
      rejectConfirmValidation({
        errors,
        setValidationErrors,
        setError,
        setConfirmUpdateDialogOpen,
        setShowFieldErrors,
      });
      return;
    }
    setConfirmUpdateDialogOpen(false);
    await doSave();
  };

  const getQuantityLabel = (quantity) => {
    const qty = parseInt(quantity) || 0;
    if (qty === 0) return { label: 'Out of Inventory', color: '#F87171' };
    if (qty <= 5)  return { label: 'Low Inventory',    color: '#FCD34D' };
    if (qty <= 20) return { label: 'Medium Inventory', color: NEON2     };
    return                { label: 'Available',        color: NEON3     };
  };

  const departmentOptions = departments.map((dept) => ({
    value: String(dept.id),
    label: dept.name,
  }));

  if (dataLoading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <Loading message={isEditMode ? 'Loading stock item' : 'Loading stock form'} size={56} variant="primary" />
    </Box>
  );
  if (!currentUser || !canManageStock) return null;

  const submitDisabled = loading
    || !formData.things.trim()
    || !formData.place.trim()
    || !formData.department_id
    || String(formData.quantity ?? '').trim() === '';
  const stockStatus = formData.quantity ? getQuantityLabel(formData.quantity) : null;
  const canSubmit = !submitDisabled;
  const { fieldHelper, fieldHasError } = createFieldErrorGate(showFieldErrors, validationErrors);

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 6 }}>

      <Header
        title={isEditMode ? 'Edit Inventory Item' : 'Create New Inventory Item'}
        subtitle={isEditMode ? 'Update stock information and quantities' : 'Add a new item to your inventory stock'}
        icon={InventoryIcon}
        showBackButton
        onBackClick={() => navigate(-1)}
        backButtonText="Back to Inventory"
      />

      <FeedbackMessage message={successMessage} severity="success" onClose={() => setSuccessMessage('')}
        sx={{ fontFamily: "'DM Sans',sans-serif", fontSize: '0.9rem', fontWeight: 500, background: alpha(SUCCESS, 0.12), border: `1px solid ${alpha(SUCCESS, 0.25)}`, color: alpha(SUCCESS, 0.95) }}
      />
      <FeedbackMessage message={error} severity="error" onClose={() => setError(null)}
        sx={{ fontFamily: "'DM Sans',sans-serif", fontSize: '0.9rem', background: alpha('#450a0a', 0.92), border: '1px solid rgba(248,113,113,0.3)', color: '#fca5a5', boxShadow: '0 4px 20px rgba(239,68,68,0.12)' }}
      />

      {formDraft.showNotice && (
        <FormDraftNotice
          savedAt={formDraft.restoredAt}
          onDiscard={formDraft.discardDraft}
          onDismiss={formDraft.dismissNotice}
        />
      )}

      <form onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

          {/* ── Stock Details (Item Info + Location & Department merged) ─────── */}
          <SectionCard title="Inventory Details" icon={<InventoryIcon />} delay={0}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

              {/* Row 1: Item Name */}
              <StyledField
                fullWidth required label="Item Name" name="things" value={formData.things}
                onChange={handleChange}
                error={fieldHasError('things')}
                helperText={fieldHelper('things')}
                InputProps={{ startAdornment: <InputAdornment position="start"><CategoryIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} /></InputAdornment> }}
              />

              {/* Row 2: Quantity + Stock Status badge */}
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2.5, alignItems: 'flex-start' }}>
                <StyledField
                  fullWidth required label="Quantity" name="quantity" type="number" value={formData.quantity}
                  onChange={handleChange}
                  error={fieldHasError('quantity')}
                  helperText={fieldHelper('quantity')}
                  sx={{ flex: 1 }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><NumbersIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} /></InputAdornment> }}
                />

                {stockStatus && (
                  <Box sx={{
                    p: 2, borderRadius: '10px',
                    background: alpha(stockStatus.color, 0.07),
                    border: `1px solid ${alpha(stockStatus.color, 0.25)}`,
                    display: 'flex', alignItems: 'center', gap: 1.5,
                    flex: 1, minHeight: '56px',
                  }}>
                    <CheckCircleIcon sx={{ color: alpha(stockStatus.color, 0.8), flexShrink: 0, fontSize: 20 }} />
                    <Box>
                      <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.62rem', fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', color: alpha(stockStatus.color, 0.6), mb: 0.25 }}>
                        Inventory Level
                      </Typography>
                      <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.875rem', color: stockStatus.color, fontWeight: 600 }}>
                        {stockStatus.label}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>

              {/* Row 3: Storage Location + Department */}
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2.5, alignItems: 'flex-start' }}>
                <StyledField
                  fullWidth required label="Storage Location" name="place" value={formData.place}
                  onChange={handleChange}
                  error={fieldHasError('place')}
                  helperText={fieldHelper('place')}
                  InputProps={{ startAdornment: <InputAdornment position="start"><LocationIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} /></InputAdornment> }}
                />

                <SearchableSelect
                  required
                  label="Department"
                  value={formData.department_id}
                  onChange={(v) => handleFieldChange('department_id', v)}
                  options={departmentOptions}
                  error={fieldHasError('department_id')}
                  helperText={fieldHelper('department_id')}
                  startIcon={<BusinessIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} />}
                />
              </Box>

              {/* Row 4: Remark (inline, no separate card) */}
              <StyledField
                fullWidth label="Remark" name="remark" value={formData.remark}
                onChange={handleChange}
                multiline rows={3}
                error={fieldHasError('remark')}
                helperText={fieldHelper('remark')}
                InputProps={{ startAdornment: <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}><DescriptionIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} /></InputAdornment> }}
              />

            </Box>
          </SectionCard>

          {/* ── Submit ─────────────────────────────────────────────────────────── */}
          <SubmitActionsCard
            statusText={isEditMode ? 'Save changes to update this stock item.' : 'Complete the form to add a new stock item.'}
            missingRequiredFields={isEditMode ? null : [!formData.things.trim() && 'Item Name', String(formData.quantity ?? '').trim() === '' && 'Quantity', !formData.place.trim() && 'Storage Location', !formData.department_id && 'Department'].filter(Boolean)}
            readyStatusText="All required fields are complete. Ready to create stock item."
            cancelLabel="Cancel"
            onCancel={() => navigate(-1)}
            cancelDisabled={loading}
            submitLabel={isEditMode ? 'Update Inventory' : 'Create Inventory Item'}
            submitDisabled={submitDisabled}
            loading={loading}
            submitStartIcon={<SaveIcon />}
            showSubmit
            submitType="submit"
            {...submitDraftProps}
          />

        </Box>
      </form>

      <ConfirmUpdateDialog
        open={confirmUpdateDialogOpen}
        onClose={() => setConfirmUpdateDialogOpen(false)}
        onConfirm={handleConfirmUpdate}
        variant="edit"
        itemType="stock"
        itemName={formData.things}
        title="Confirm Update"
        description="Are you sure you want to update this stock item?"
        confirmLabel="Update Inventory"
        loading={loading}
        confirmDisabled={!canSubmit}
      />

    </Container>
  );
};

export default StockForm;
